import FormActionButtons from '@components/FormActionButtons';
import { Box, FormGroup, FormLabel, TextField, Typography, useTheme } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

interface SalaryFormValues {
    currentSalary: string;
    expectedSalary: string;
}

interface SalaryFormPayload {
    currentSalary: number | null;
    expectedSalary: number | null;
}

interface TalentSalaryFormProps {
    initialCurrentSalary?: number | null;
    initialExpectedSalary?: number | null;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (payload: SalaryFormPayload) => Promise<void> | void;
}

const formatInitialValue = (value?: number | null) => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
};

const parseSalaryValue = (value: string): number | null => {
    const normalized = value.replace(/\s/g, '').replace(',', '.').trim();

    if (!normalized) {
        return null;
    }

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
};

export const TalentSalaryForm = ({
    initialCurrentSalary,
    initialExpectedSalary,
    loading = false,
    onCancel,
    onSubmit,
}: TalentSalaryFormProps) => {
    const theme = useTheme();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<SalaryFormValues>({
        mode: 'onChange',
        defaultValues: {
            currentSalary: formatInitialValue(initialCurrentSalary),
            expectedSalary: formatInitialValue(initialExpectedSalary),
        },
    });

    const submitHandler = async (values: SalaryFormValues) => {
        await onSubmit({
            currentSalary: parseSalaryValue(values.currentSalary),
            expectedSalary: parseSalaryValue(values.expectedSalary),
        });
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(submitHandler)}
            sx={{
                mt: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}>
            <Controller
                name="currentSalary"
                control={control}
                rules={{
                    validate: value => {
                        if (!value.trim()) {
                            return true;
                        }

                        const parsed = parseSalaryValue(value);
                        if (parsed === null || parsed < 0) {
                            return 'Le salaire actuel doit etre un nombre positif';
                        }

                        return true;
                    },
                }}
                render={({ field }) => (
                    <FormGroup>
                        <FormLabel>
                            Salaire actuel
                        </FormLabel>
                        <TextField
                            {...field}
                            type="text"
                            placeholder="Ex: 1750"
                            error={!!errors.currentSalary}
                            helperText={errors.currentSalary?.message}
                            fullWidth
                            size="small"
                        />
                    </FormGroup>
                )}
            />

            <Controller
                name="expectedSalary"
                control={control}
                rules={{
                    validate: value => {
                        if (!value.trim()) {
                            return true;
                        }

                        const parsed = parseSalaryValue(value);
                        if (parsed === null || parsed < 0) {
                            return 'Le salaire attendu doit etre un nombre positif';
                        }

                        return true;
                    },
                }}
                render={({ field }) => (
                    <FormGroup>
                        <FormLabel>
                            Salaire attendu
                        </FormLabel>
                        <TextField
                            {...field}
                            type="text"
                            placeholder="Ex: 2320"
                            error={!!errors.expectedSalary}
                            helperText={errors.expectedSalary?.message}
                            fullWidth
                            size="small"
                        />
                    </FormGroup>
                )}
            />

            <FormActionButtons
                onCancel={onCancel}
                loading={loading}
                containerSx={{ mt: 0.5 }}
                size="small"
            />
        </Box>
    );
};

export default TalentSalaryForm;
